import TablePaginadaBasic from "@/Components/Base/Table/TablePaginadaBasic";
import { usePessoaBloqueioListarPagina, PessoaBloqueioListarPaginaFiltros } from "@/Services/Pessoa/PessoaBloqueioService";
import { CheckOutlined } from "@ant-design/icons";
import { Form, Row, Col, Button, Space, Select } from "antd";
import { useEffect, useState } from 'react';
import PessoaBloqueioDesbloquearModal from "./PessoaBloqueioDesbloquearModal";
import { PaginaInfo } from "@/Models/Utils/Pagina/Pagina";
import BloqueioTipoSelect from "./BloqueioTipo/BloqueioTipoSelect";
import { PessoaSelect } from "../PessoaSelect";
import { EnumPessoaTipo } from "@/Models/Pessoa/Pessoa";

const definedProps = (obj:any) => Object.fromEntries(
  Object.entries(obj ?? {}).filter(([k, v]) => v !== undefined)
);

export interface IPessoaBloqueioTableFiltro extends Omit<PessoaBloqueioListarPaginaFiltros, "paginaInfo">
{

}
export interface IPessoaBloqueioTableProps
{
    filtros ?: IPessoaBloqueioTableFiltro;
    habilitarFiltros ?: boolean;
}
export default function PessoaBloqueioTable ({
    habilitarFiltros,
    filtros : filtrosParam
}:IPessoaBloqueioTableProps) {
    habilitarFiltros = habilitarFiltros ?? true;

    const [form] = Form.useForm<PessoaBloqueioListarPaginaFiltros>();
    const filtros = Form.useWatch([], form);

    const {
        data,
        refetch,
        isLoading,
        isFetching
    } = usePessoaBloqueioListarPagina({
        ...Object.assign({}, filtros, definedProps(filtrosParam)),
        _optimisticResults : "optimistic",
        ativo : true,
        enabled : false
    });

    const loading = isFetching || isLoading;

    useEffect(() => { if (filtros?.paginaInfo) refetch(); }, [filtros?.paginaInfo]);

    return <Row>
        {process.env.NODE_ENV === "development" && <Col>{JSON.stringify(filtros ?? {})}</Col>}
        <Col span={24}>
            <Form
                form={form}
                layout={"vertical"}
                autoComplete={"off"}
                size="small"
                initialValues={{
                    paginaInfo : new PaginaInfo()
                }}
                onFinish={(values) => {
                    if (values.paginaInfo?.pagina === 1)
                        refetch();
                    else
                        form.setFieldValue(["paginaInfo", "pagina"], 1);
                }}
            >
                <Form.Item hidden name="paginaInfo" label="paginaInfo">
                    <input />
                </Form.Item>
                {habilitarFiltros && <Row gutter={4} wrap justify="start" align="bottom">
                    <Col xs={24} sm={8} md={4}>
                        <Form.Item<PessoaBloqueioListarPaginaFiltros> name={["pessoaBloqueio", "codBloqueioTipo"]} label="Tipo Bloqueio">
                            <BloqueioTipoSelect />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8} md={5}>
                        <Form.Item<PessoaBloqueioListarPaginaFiltros> name={["pessoaBloqueio", "codPessoa"]} label="Responsável">
                            <PessoaSelect
                                tipoPessoa={EnumPessoaTipo.RESPONSAVEL}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={8} md={5}>
                        <Form.Item<PessoaBloqueioListarPaginaFiltros> name={["bloqueado"]} label="Situação" initialValue={true}>
                            <Select options={[
                                {
                                    value : null,
                                    label : "Todos"
                                },
                                {
                                    value : true,
                                    label : "Bloqueados"
                                },
                                {
                                    value : false,
                                    label : "Desbloqueados"
                                }
                            ]}>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={"auto"}>
                        <Form.Item<PessoaBloqueioListarPaginaFiltros>>
                            <Button type="primary" htmlType="submit">Listar</Button>
                        </Form.Item>
                    </Col>
                </Row>}
            </Form>
        </Col>
        
        <Col span={24}>
            <TablePaginadaBasic 
                bordered={true}
                locale={{
                    emptyText : 'Nenhuma bloqueio encontrada.'
                }}
                disableResponsiveness={true}
                loading={loading}
                rowKey={(k) => k.codigo}
                size={"small"}
                tableLayout="auto"
                scroll={{
                    x: true,
                }}
                showHeader={true}
                dataSource={data}
                columns={[
                    {
                        key : "Criado",
                        title : "Criado em",
                        dataIndex : "dataCriacao",
                        ellipsis : {
                            showTitle : true
                        },
                        render(value:Date) {
                            return value.toLocaleDateString();
                        },
                    },
                    {
                        key : "tipo",
                        title : "Tipo",
                        minWidth : "80px",
                        dataIndex : ["bloqueioTipo", "nome"],
                        ellipsis : {
                            showTitle : true
                        },
                        render(value) {
                            return value;
                        },
                    },
                    {
                        key : "Pessoa",
                        title : "Pessoa",
                        minWidth : "100px",
                        ellipsis : {
                            showTitle : false
                        },
                        dataIndex : ["pessoa", "nomeExibicao"],
                        render(value) {
                            return value;
                        },
                    },
                    {
                        key : "Situação",
                        title : "Situação",
                        ellipsis : {
                            showTitle : true
                        },
                        dataIndex : ["bloqueado"],
                        render(value) {
                            return value ? "Bloqueado" : "Desbloqueado";
                        },
                    },
                    {
                        key : "DesbloqueadoEm",
                        title : "Liberado em",
                        ellipsis : {
                            showTitle : true
                        },
                        dataIndex : "dataLiberacao",
                        render(value:Date) {
                            return value?.toLocaleDateString?.() ?? " - ";
                        },
                    },
                    {
                        key : "acoes",
                        dataIndex : "codigo",
                        minWidth : "20px",
                        fixed : "right",
                        render(value:number) {
                            return <Row justify={"center"}>
                                <PessoaBloqueioDesbloquearModal codigo={value} onSuccess={() => form.submit()} />
                            </Row>;
                        },
                    }
                ]}
                onChange={(pagina) => {
                    form.setFieldsValue({
                        paginaInfo : {
                            pagina : pagina.current ?? 1,
                            tamanho : pagina.pageSize ?? 20
                        }
                    } as any);
                }} 
            />
        </Col>
    </Row>
}